/**
 * La Nefeli — theme core.
 *
 * Loaded first with `defer`. Deferred classic scripts execute in document
 * order, so everything here is defined before any feature script runs. This is
 * deliberately NOT an ES module: relative imports between theme assets resolve
 * against a cache-busted CDN URL, and a module graph costs extra round trips on
 * 4G for no benefit at this size.
 *
 * Exposes `window.Theme`.
 *
 * IMPORTANT: <script> tags inside Section-Rendering-API HTML never execute.
 * Bind behaviour in a custom element's connectedCallback (which fires when the
 * replaced HTML contains the element) or by delegation from an ancestor that is
 * never replaced. That is why every interactive region is a custom element.
 */
(function () {
  'use strict';

  /* ----------------------------------------------------------------- data */

  /**
   * Routes and strings, from `snippets/theme-data.liquid`. No URL is ever
   * hardcoded here — `/cart/change.js` is only correct until a locale or market
   * prefix appears, and the `routes` object already knows the difference.
   */
  function readThemeData() {
    const fallback = { routes: {}, strings: {} };
    const node = document.getElementById('theme-data');
    if (!node) return fallback;
    try {
      const parsed = JSON.parse(node.textContent);
      return {
        routes: parsed.routes || {},
        strings: parsed.strings || {},
      };
    } catch (error) {
      return fallback;
    }
  }

  const themeData = readThemeData();

  /* ---------------------------------------------------------------- utils */

  function debounce(fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /**
   * Announce a message to screen readers through the single live region in
   * layout/theme.liquid. Cart, filter and search updates all route through here
   * rather than each inventing its own region.
   */
  function announce(message) {
    const region = document.getElementById('a11y-announcer');
    if (!region) return;
    // Clearing first forces AT to re-read an identical message.
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  /* -------------------------------------------------- scroll lock (iOS) */

  /**
   * critical.css already hides overflow via :has() when a scroll-lock dialog is
   * open, which is enough on desktop. iOS Safari ignores that on the body, so we
   * also pin the body and restore the scroll position on release. Reference
   * counted, because a size chart can open on top of a cart drawer.
   */
  let scrollPosition = 0;
  let lockCount = 0;

  function lockScroll() {
    if (lockCount++ > 0) return;
    scrollPosition = window.scrollY;
    const body = document.body;
    body.style.position = 'fixed';
    body.style.insetInlineStart = '0';
    body.style.insetBlockStart = `-${scrollPosition}px`;
    body.style.width = '100%';
  }

  function unlockScroll() {
    if (lockCount > 0 && --lockCount > 0) return;
    lockCount = 0;
    const body = document.body;
    body.style.position = '';
    body.style.insetInlineStart = '';
    body.style.insetBlockStart = '';
    body.style.width = '';
    window.scrollTo(0, scrollPosition);
  }

  /* --------------------------------------------- Section Rendering API */

  /**
   * Fetch one or more rendered sections.
   * @param {string} url - Storefront URL providing the context (collection, product, cart…).
   * @param {string[]} sections - Section ids to render.
   * @returns {Promise<Object>} Map of section id to HTML string.
   */
  async function fetchSections(url, sections) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('sections', sections.join(','));
    const response = await fetch(target.toString(), {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
    return response.json();
  }

  /**
   * Fetch a URL and return it as a parsed document, for swapping parts of a
   * full page render (facets, variant changes).
   */
  async function fetchDocument(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /**
   * Replace the contents of `selector` in the live page with the same node from
   * `source`. Returns true when the swap happened.
   */
  function swap(source, selector) {
    const incoming = source.querySelector(selector);
    const current = document.querySelector(selector);
    if (!incoming || !current) return false;
    current.innerHTML = incoming.innerHTML;
    return true;
  }

  /* ------------------------------------------------------- base element */

  class ThemeElement extends HTMLElement {
    connectedCallback() {
      // Idempotent: the editor and SRAPI can reconnect the same node.
      if (this.initialised) return;
      this.initialised = true;
      this.controller = new AbortController();
      if (typeof this.init === 'function') this.init();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.initialised = false;
      if (typeof this.destroy === 'function') this.destroy();
    }

    /** addEventListener that is cleaned up automatically on disconnect. */
    on(target, type, handler, options) {
      target.addEventListener(
        type,
        handler,
        Object.assign({}, options, { signal: this.controller.signal })
      );
    }

    $(selector) {
      return this.querySelector(selector);
    }

    $$(selector) {
      return Array.from(this.querySelectorAll(selector));
    }
  }

  /* ----------------------------------------------------- dialog / drawer */

  /**
   * Base for every drawer, sheet and modal in the theme: menu drawer, search
   * sheet, filter sheet, sort sheet, cart drawer, quick-add sheet, size chart.
   *
   * Wraps native <dialog> + showModal(), so focus trapping, `inert` on the rest
   * of the page, and Escape-to-close come from the platform rather than from
   * hand-rolled JS. We add only focus restore and iOS scroll lock.
   *
   * Expected markup:
   *   <drawer-element id="MenuDrawer">
   *     <dialog scroll-lock>
   *       <button data-dialog-close>…</button>
   *     </dialog>
   *   </drawer-element>
   *
   * Opened from anywhere with: <button data-dialog-open="MenuDrawer">
   */
  class DialogElement extends ThemeElement {
    init() {
      this.dialog = this.$('dialog');
      if (!this.dialog) return;

      // One delegated listener on the dialog, which is never replaced, rather
      // than one per button. The cart drawer's whole inner is the Section
      // Rendering target, so a close button bound at init is detached — and
      // silently inert — from the first quantity change onwards. Same reason
      // `[data-dialog-open]` is delegated from the document below.
      this.on(this.dialog, 'click', (event) => {
        // The backdrop is the dialog element itself.
        if (event.target === this.dialog) {
          this.close();
          return;
        }
        if (event.target.closest && event.target.closest('[data-dialog-close]')) {
          this.close();
        }
      });

      // Fires for Escape too, so focus restore lives here rather than close().
      this.on(this.dialog, 'close', () => {
        if (this.locksScroll) unlockScroll();
        this.locksScroll = false;
        this.restoreFocus();
        this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      });
    }

    /**
     * A Section-Rendering swap can replace the very button that opened this
     * dialog while it is open — the filter sheet updates its own trigger's
     * count. The remembered node is then detached and focusing it silently
     * drops the customer at the top of the document, so fall back to whatever
     * now opens this dialog.
     */
    restoreFocus() {
      let opener = this.opener;
      if (!opener || !opener.isConnected) {
        opener = this.id ? document.querySelector('[data-dialog-open="' + this.id + '"]') : null;
      }
      if (opener) opener.focus();
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      this.opener = trigger || document.activeElement;
      this.dialog.showModal();
      if (this.dialog.hasAttribute('scroll-lock')) {
        this.locksScroll = true;
        lockScroll();
      }
      const first = this.$('[data-dialog-focus]');
      if (first) first.focus();
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
    }

    get isOpen() {
      return Boolean(this.dialog && this.dialog.open);
    }
  }

  if (!customElements.get('drawer-element')) {
    customElements.define('drawer-element', class extends DialogElement {});
  }

  // One delegated listener for every open trigger on the page, so triggers
  // inside SRAPI-replaced HTML keep working without rebinding.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-dialog-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.getAttribute('data-dialog-open'));
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open(trigger);
    }
  });

  /**
   * Quantity stepper. The number input is the control; the buttons are a
   * convenience over it and disable themselves at the boundaries rather than
   * letting a customer drive the value out of range.
   */
  /* Inside theme.js the base class is the local binding: `window.Theme` is not
     assigned until the end of this IIFE. */
  class QuantityInput extends ThemeElement {
    init() {
      this.input = this.querySelector('[data-quantity-input]');
      if (!this.input) return;
      this.buttons = this.$$('[data-quantity-step]');

      this.buttons.forEach((button) => {
        this.on(button, 'click', () => {
          const step = Number(button.dataset.quantityStep);
          this.input.value = String(this.clamp(Number(this.input.value) + step));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.sync();
        });
      });

      this.on(this.input, 'change', () => {
        this.input.value = String(this.clamp(Number(this.input.value)));
        this.sync();
      });

      this.sync();
    }

    clamp(value) {
      const min = Number(this.input.min) || 1;
      const max = Number(this.input.max) || Infinity;
      if (isNaN(value)) return min;
      return Math.min(Math.max(Math.round(value), min), max);
    }

    sync() {
      const value = Number(this.input.value);
      const min = Number(this.input.min) || 1;
      const max = Number(this.input.max) || Infinity;
      this.buttons.forEach((button) => {
        const step = Number(button.dataset.quantityStep);
        button.disabled = step < 0 ? value <= min : value >= max;
      });
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  /* -------------------------------------------------------- theme editor */

  // Close dialogs in a section that is being removed, and open the one a
  // merchant just selected a block inside.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('drawer-element').forEach((el) => el.close());
  });

  function revealContainingDialog(event) {
    const dialog = event.target.closest('drawer-element');
    if (dialog && !dialog.isOpen) dialog.open();
  }

  document.addEventListener('shopify:block:select', revealContainingDialog);
  document.addEventListener('shopify:section:select', revealContainingDialog);

  document.addEventListener('shopify:section:deselect', (event) => {
    event.target.querySelectorAll('drawer-element').forEach((el) => el.close());
  });

  /* ---------------------------------------------------------------- export */

  window.Theme = {
    routes: themeData.routes,
    strings: themeData.strings,
    ThemeElement: ThemeElement,
    DialogElement: DialogElement,
    announce: announce,
    debounce: debounce,
    fetchSections: fetchSections,
    fetchDocument: fetchDocument,
    swap: swap,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
  };
})();
